import { useCallback, useEffect } from 'react';

type TouchControlsProps = {
  movePlayer: (dir: number) => void;
  dropPlayer: () => void;
  hardDrop: () => void;
  playerRotate: (stage: any, dir: number) => void;
  stage: any;
  clockwiseRotation: boolean;
  paused: boolean;
  gameOver: boolean;
};

export const useTouchControls = ({
  movePlayer,
  dropPlayer,
  hardDrop,
  playerRotate,
  stage,
  clockwiseRotation,
  paused,
  gameOver
}: TouchControlsProps) => {
  
  // Track touch start position for swipe detection
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartTime = 0;
  
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (paused || gameOver) return;
    
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchStartTime = Date.now();
  }, [paused, gameOver]);
  
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (paused || gameOver) return;
    
    // Prevent scrolling when playing
    e.preventDefault();
  }, [paused, gameOver]);
  
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (paused || gameOver) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const touchEndTime = Date.now();
    
    const deltaX = touchEndX - touchStartX;
    const deltaY = touchEndY - touchStartY;
    const deltaTime = touchEndTime - touchStartTime;
    
    // Minimum distance for a swipe
    const minDistance = 50;
    // Maximum time for a swipe (ms)
    const maxTime = 300;
    
    // Detect tap (quick touch with minimal movement)
    if (Math.abs(deltaX) < 20 && Math.abs(deltaY) < 20 && deltaTime < 200) {
      // Tap to rotate
      playerRotate(stage, clockwiseRotation ? 1 : -1);
      return;
    }
    
    // Check if it's a valid swipe
    if (deltaTime < maxTime) {
      // Horizontal swipe
      if (Math.abs(deltaX) > minDistance && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > 0) {
          // Right swipe
          movePlayer(1);
        } else {
          // Left swipe
          movePlayer(-1);
        }
      }
      
      // Vertical swipe
      if (Math.abs(deltaY) > minDistance && Math.abs(deltaY) > Math.abs(deltaX)) {
        if (deltaY > 0) {
          // Down swipe
          dropPlayer();
        } else {
          // Up swipe (hard drop)
          hardDrop();
        }
      }
    }
  }, [movePlayer, dropPlayer, hardDrop, playerRotate, stage, clockwiseRotation, paused, gameOver]);
  
  useEffect(() => {
    // Add touch event listeners to the document
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    return () => {
      // Clean up event listeners
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
};